import type { SettleGroup, Settlement, Statement, StatementLine } from "../../api/types";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatDate, formatTime } from "../../lib/time";

const GROUPS: readonly SettleGroup[] = ["cash", "bank_qr", "prepaid"];

function Line({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="kv">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

/** "Contant", "Bancontact + Online"; nothing paid (all free): "—". */
export function methodsOf(line: Pick<StatementLine, "methods">): string {
  return line.methods.length === 0 ? "—" : line.methods.map((method) => t.paymentMethods[method]).join(" + ");
}

/** "Kerkstraat 3 bus 2" of "Kerkstraat 3 bus 2, Tielrode": the street and number, without the municipality. */
export const streetOf = (address: string): string => address.split(", ")[0] ?? address;

interface Props {
  /** "Naam op de bon" of the shop. */
  shop: string;
  /** What the courier has to settle now, or a settlement (the copy, "afgerekend"). */
  statement: Statement | Settlement;
  /** When the bon is printed (the moment of the settlement for a copy). */
  at: string;
}

/**
 * The afrekenbon of a courier (prototype openCourierBon): the shop, AFREKENING KOERIER, the courier and
 * when, every delivery under CONTANT ONTVANGEN, BANK-QR or VOORAF BETAALD with a subtotal, the number of
 * rides, AF TE GEVEN CONTANT and who settled (on the copy); no line for a signature any more (Furkan, 18/10/2026).
 */
export function CourierBon({ shop, statement, at }: Props) {
  const text = t.couriers.bonDialog;
  const settledBy = "settledBy" in statement ? statement.settledBy : null;
  return (
    <article className="bon cbon" aria-label={text.heading}>
      <div className="c blk cb-shop">{shop}</div>
      <div className="c blk cb-title">{text.heading}</div>
      <div className="c">
        <b>{statement.courier.name}</b>
        <br />
        {formatDate(at)} {formatTime(at)} · {settledBy === null ? text.pending : text.settled}
      </div>
      <hr />
      {GROUPS.map((group) => {
        const lines = statement.lines.filter((line) => line.group === group);
        if (lines.length === 0) return null;
        return (
          <div key={group}>
            <div className="blk">
              {text.groups[group]} ({lines.length})
            </div>
            {/* one short line a ride, in small letters: when, the code, the street and number, the amount; the
                group above says how it was paid (Furkan, 18/10/2026: the bon was far too long) */}
            {lines.map((line) => (
              <div className="it cbit" key={`${line.orderId}-${group}`}>
                <span>{formatTime(line.deliveredAt)}</span>
                <b>{line.code}</b>
                <span className="cb-street">{streetOf(line.address)}</span>
                <span className="pr">{formatEuro(line.amountCents)}</span>
              </div>
            ))}
            <Line
              label={text.subtotals[group]}
              value={formatEuro(lines.reduce((total, line) => total + line.amountCents, 0))}
            />
            <hr />
          </div>
        );
      })}
      <Line label={text.rides} value={statement.rides} />
      <div className="kv blk cb-big">
        <span>{text.handOver}</span>
        <span>{formatEuro(statement.cashCents)}</span>
      </div>
      {settledBy !== null && <Line label={text.by} value={settledBy} />}
      <div className="bontafo">
        <img src="/tafo-logo.png" alt="Tafo" />
        <span>{t.receipt.tafo}</span>
      </div>
    </article>
  );
}
